import type { AnyID } from '@types'
import { findPenetratingNeighbor } from '@utils/modularSnap/collisionGuard'
import { toVector3 } from '@utils/conversion/toVector3'
import { degrees } from '@utils/generalMaths/geometry'
import { Matrix4, Quaternion, Vector3 } from 'three'
import { resnapModularSectionAfterRotate, type ResnapSectionStore } from './resnapSection'

/** Ángulo Y en grados (misma convención que editItemAngle / ItemAngleInput). */
export function readItemYAngleDegrees(matrix: Matrix4): number {
  const basisX = toVector3()
  matrix.extractBasis(basisX, toVector3(), toVector3())
  const xRot = toVector3([1, 0, 0]).angleTo(basisX)
  const crossProduct = toVector3([1, 0, 0]).cross(basisX)
  return (degrees(xRot * -Math.sign(crossProduct.y)) + 360) % 360
}

type RotateSectionStore = ResnapSectionStore & {
  editItemAngle: (id: AnyID, angle: number) => void
}

/** Gira 90° la sección en su pivote (origen del módulo), sin mover el origen. */
export function rotateModularSection90(
  itemId: AnyID,
  store: RotateSectionStore,
  options?: { resnapToNearest?: boolean },
): number | null {
  const item = store.items.get(String(itemId)) as
    | { matrix?: Matrix4; type?: string; width?: number; height?: number; thickness?: number }
    | undefined
  if (!item?.matrix || item.type !== 'linkedItem') return null

  const beforeMatrix = item.matrix.clone()
  const beforeRotation = readItemYAngleDegrees(beforeMatrix)

  const current = beforeRotation
  const next = (current + 90) % 360
  store.editItemAngle(itemId, next)

  const updated = store.items.get(String(itemId)) as { matrix?: Matrix4 } | undefined
  if (!updated?.matrix) return next

  store.setOBB(itemId)
  store.updateSelectedObjectsPosition(itemId, updated.matrix.clone())

  if (options?.resnapToNearest) {
    resnapModularSectionAfterRotate(itemId, store)
  }

  const after = store.items.get(String(itemId)) as
    | {
        matrix?: Matrix4
        type?: string
        width: number
        height: number
        thickness: number
      }
    | undefined

  if (after?.matrix && store.collisionableItems.has(itemId)) {
    const penetratedBy = findPenetratingNeighbor(
      itemId,
      after.matrix,
      after,
      store.items as Map<string, any>,
      store.collisionableItems,
    )
    if (penetratedBy) {
      store.editItemAngle(itemId, beforeRotation)
      const reverted = store.items.get(String(itemId))
      if (reverted?.matrix) {
        store.setOBB(itemId)
        store.updateSelectedObjectsPosition(itemId, reverted.matrix.clone())
      }
      return beforeRotation
    }
  }

  return next
}
